import { createRuntimeError, getErrorMessage } from "./useChatControllerHelpers.js";
export function createConversationActions(params) {
  const {
    store,
    runtimeRef,
    setRuntimeError,
    stopStreaming,
    conversationNavigationRequestIdRef,
    conversationLoadRequestIdRef,
    historyLoadRequestIdRef
  } = params;
  async function loadConversationMessages(conversationId, options = {}) {
    conversationLoadRequestIdRef.current += 1;
    const requestId = conversationLoadRequestIdRef.current;
    const {
      resetWhenUndefined = true
    } = options;
    const shouldResetMessages = resetWhenUndefined && store.parameters.messages === undefined;
    if (conversationId == null) {
      if (shouldResetMessages) {
        store.resetMessages();
      }
      return;
    }
    if (!runtimeRef.current.adapter.listMessages) {
      if (shouldResetMessages) {
        store.resetMessages();
      }
      return;
    }
    if (shouldResetMessages) {
      store.resetMessages();
    }
    historyLoadRequestIdRef.current += 1;
    const historyRequestId = historyLoadRequestIdRef.current;
    store.setHistoryLoading(true);
    try {
      const result = await runtimeRef.current.adapter.listMessages({
        conversationId,
        direction: 'backward'
      });
      if (requestId !== conversationLoadRequestIdRef.current || store.state.activeConversationId !== conversationId) {
        return;
      }
      store.setMessages(result.messages);
      store.setHistoryState({
        cursor: result.cursor,
        hasMore: result.hasMore ?? false
      });
      store.setError(null);
    } catch (error) {
      if (requestId !== conversationLoadRequestIdRef.current || store.state.activeConversationId !== conversationId) {
        return;
      }
      setRuntimeError(createRuntimeError('HISTORY_ERROR', getErrorMessage('Unable to load conversation history.', error), 'history', true, true, {
        conversationId
      }));
    } finally {
      if (historyRequestId === historyLoadRequestIdRef.current) {
        store.setHistoryLoading(false);
      }
    }
  }
  async function loadMoreHistory() {
    const conversationId = store.state.activeConversationId;
    if (!conversationId) {
      return;
    }
    if (!runtimeRef.current.adapter.listMessages && !runtimeRef.current.adapter.loadMore) {
      return;
    }
    historyLoadRequestIdRef.current += 1;
    const historyRequestId = historyLoadRequestIdRef.current;
    store.setHistoryLoading(true);
    try {
      let result;
      if (runtimeRef.current.adapter.listMessages) {
        result = await runtimeRef.current.adapter.listMessages({
          conversationId,
          cursor: store.state.historyCursor,
          direction: 'backward'
        });
      } else if (runtimeRef.current.adapter.loadMore) {
        result = await runtimeRef.current.adapter.loadMore(store.state.historyCursor);
      }
      if (!result) {
        return;
      }
      if (store.state.activeConversationId !== conversationId) {
        return;
      }
      store.prependMessages(result.messages);
      store.setHistoryState({
        cursor: result.cursor,
        hasMore: result.hasMore ?? false
      });
      store.setError(null);
    } catch (error) {
      if (store.state.activeConversationId !== conversationId) {
        return;
      }
      setRuntimeError(createRuntimeError('HISTORY_ERROR', getErrorMessage('Unable to load more message history.', error), 'history', true, true, {
        conversationId,
        cursor: store.state.historyCursor
      }));
    } finally {
      if (historyRequestId === historyLoadRequestIdRef.current) {
        store.setHistoryLoading(false);
      }
    }
  }
  async function setActiveConversation(id) {
    if (store.state.activeConversationId === id) {
      return;
    }
    stopStreaming();
    conversationNavigationRequestIdRef.current += 1;
    store.setActiveConversation(id);
    await loadConversationMessages(id);
  }
  return {
    loadConversationMessages,
    loadMoreHistory,
    setActiveConversation
  };
}